import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-bleed crossfade image slider. Auto-advances every `interval` ms,
 * pauses on hover/focus, and exposes prev/next + dot indicators.
 */
export default function ImageSlider({
  images = [],
  captions,
  interval = 5500,
  className = "",
  testid = "image-slider",
  aspect = "aspect-[16/9] md:aspect-[21/9]",
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = images.length;
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % total);
    }, interval);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, interval, total]);

  if (total === 0) return null;

  const goTo = (i) => setIndex((i + total) % total);

  return (
    <section
      className={`relative overflow-hidden bg-[#111] ${className}`}
      data-testid={testid}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className={`relative w-full ${aspect}`}>
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden={i !== index}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-out"
            style={{ opacity: i === index ? 1 : 0 }}
          />
        ))}

        {captions && captions.length === images.length && (
          <>
            <div className="absolute inset-0 bg-black/30" />
            {captions.map((label, i) => (
              <div
                key={i}
                aria-hidden={i !== index}
                className="absolute inset-0 flex items-center justify-center transition-opacity duration-[1200ms] ease-out pointer-events-none"
                style={{ opacity: i === index ? 1 : 0 }}
              >
                <h3 className="font-heading text-white tracking-[0.3em] uppercase text-2xl md:text-4xl lg:text-5xl font-medium">
                  {label}
                </h3>
              </div>
            ))}
          </>
        )}

        {total > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              data-testid={`${testid}-prev`}
              onClick={() => goTo(index - 1)}
              className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-black/30 backdrop-blur-sm border border-white/30 text-white opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/50"
              style={{ opacity: paused ? 1 : 0 }}
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="Next image"
              data-testid={`${testid}-next`}
              onClick={() => goTo(index + 1)}
              className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-black/30 backdrop-blur-sm border border-white/30 text-white transition-opacity duration-300 hover:bg-black/50"
              style={{ opacity: paused ? 1 : 0 }}
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to image ${i + 1}`}
                  data-testid={`${testid}-dot-${i}`}
                  onClick={() => goTo(i)}
                  className={`h-px transition-all duration-500 ${
                    i === index ? "w-12 bg-white" : "w-6 bg-white/40 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
